import clsx from 'clsx';
import {
  CSSProperties,
  Dispatch,
  FC,
  MouseEvent,
  SetStateAction,
  SyntheticEvent,
} from 'react';
import { useTranslation } from 'react-i18next';

import { StatusChangeDialog } from 'src/components/assignments/status-change-dialog';
import { StreamCardContent } from 'src/components/assignments/stream-card-content';
import { Tooltip } from 'src/components/tooltip';
import {
  DEFAULT_THUMBNAIL_COLOR,
  THUMBNAIL_COLOR_BY_DOMAIN,
} from 'src/constants/color.constants';
import { useCommonXapiContextPayloadHook } from 'src/hooks/use-common-xapi-context-payload.hook';
import { useIsMiddleSchoolView } from 'src/hooks/use-is-middle-school-view';
import {
  AssignmentColorProps,
  AssignmentsViewOptions,
  CurriculumAssignmentContent,
  DesmosAssignment,
  DesmosAssignmentContent,
  SingleAssignment,
} from 'src/interfaces/assignment.types';
import { TaughtLessonAssignment } from 'src/interfaces/class-stats.types';
import {
  AssignmentCommonXApiContextPayload,
  XApiEventKeys,
} from 'src/interfaces/event.types';
import { GRADES } from 'src/interfaces/grades.types';
import AuthService from 'src/services/api/auth.service';
import eventService from 'src/services/event.service';
import {
  getAssignmentDomain,
  getIsAssignmentDueDateExpired,
  isDesmosAssignmentContent,
} from 'src/utils/assignments.utils';
import { handleSpaceClick } from 'src/utils/keyboard.utils';
import { classroomURLs, curriculumURLs } from 'src/utils/url.utils';

import './stream-card.scss';

export interface Props {
  assignment: SingleAssignment | TaughtLessonAssignment | DesmosAssignment;
  assignmentDataForEventPayload: AssignmentCommonXApiContextPayload;
  onStreamLinkClick: (
    assignmentLink: string,
    e: MouseEvent<HTMLElement>,
  ) => Promise<unknown>;
  grade: GRADES;
  color: AssignmentColorProps;
  isStatusChangeDialogVisible?: boolean;
  setIsStatusChangeDialogVisible?: Dispatch<SetStateAction<boolean>>;
  onAssignmentComplete?: (assignmentLink: string, e: SyntheticEvent) => void;
  isCardRemovalAnimationInProgress?: boolean;
  k5Section?: AssignmentsViewOptions.TODO | AssignmentsViewOptions.PAST_WORK;
}

const StreamCard: FC<Props> = ({
  assignment,
  assignmentDataForEventPayload,
  onStreamLinkClick,
  grade,
  color,
  isStatusChangeDialogVisible,
  setIsStatusChangeDialogVisible,
  onAssignmentComplete,
  isCardRemovalAnimationInProgress,
  k5Section,
}) => {
  const { t } = useTranslation();
  const isMiddleSchoolView = useIsMiddleSchoolView();

  const isPastWorkSectionActive =
    k5Section === AssignmentsViewOptions.PAST_WORK;
  const isToDoSectionActive = k5Section === AssignmentsViewOptions.TODO;

  const isStudent = AuthService.isStudent();

  const { content, isHandedInmCLASSAssessment } = assignment;

  const rawDomain = 'domain' in content ? content.domain : undefined;
  const domain = getAssignmentDomain(rawDomain);
  const thumbnailBgColor = domain
    ? THUMBNAIL_COLOR_BY_DOMAIN[domain.toLowerCase()]
    : DEFAULT_THUMBNAIL_COLOR;

  const commonXapiContextPayload = useCommonXapiContextPayloadHook();

  const isMiddleSchoolMathTaughtLesson =
    isMiddleSchoolView &&
    assignment.type === 'TaughtLessonAssignment' &&
    content.domain === 'Math';

  const isAssignmentDueDateExpired =
    assignment.type !== 'TaughtLessonAssignment' &&
    getIsAssignmentDueDateExpired(assignment);
  const isCardRemovedFromTodo =
    isCardRemovalAnimationInProgress && isToDoSectionActive;
  const shouldReducePastWorkCard =
    isStatusChangeDialogVisible &&
    isPastWorkSectionActive &&
    !isAssignmentDueDateExpired;

  const getAssignmentLinkHref = (
    content: CurriculumAssignmentContent | DesmosAssignmentContent,
  ) => {
    // Handle external_link type specifically
    if (
      'contentType' in content &&
      content.contentType === 'external_link' &&
      'studentURL' in content &&
      content.studentURL
    ) {
      return content.studentURL;
    }

    // Standard Desmos handling for other types
    if (isDesmosAssignmentContent(content)) {
      const desmosUrl = isStudent
        ? content.studentGreetingURL
        : content.dashboardURL;

      return desmosUrl ?? '';
    }

    if (content.isCurriculumCustomContent && content.customContentId) {
      return curriculumURLs.getCustomContentUrl(content.customContentId);
    }

    if (isMiddleSchoolMathTaughtLesson) {
      return curriculumURLs.getLessonBriefURL(
        content.unitKey,
        content.lessonKey,
        content.chapterKey,
      );
    }

    return classroomURLs.getURLFromContent(content);
  };

  const assignmentDetailsStyles = {
    '--dynamic-todo-assignment-color': 'var(--new-todo-assignment-color)',
    '--dynamic-done-assignment-color': 'var(--new-done-assignment-color)',
    '--done-assignment-color': 'var(--new-done-assignment-color)',
    '--todo-assignment-color': 'var(--new-todo-assignment-color)',
    '--img-color': thumbnailBgColor,
    '--dynamic-todo-assignment-date-color': color.date,
  } as CSSProperties;

  const handleCardClickRedirect = (e: MouseEvent<HTMLElement>) => {
    if (e.type === 'contextmenu') return;

    window.location.assign(getAssignmentLinkHref(content));
  };

  const handleCardClick = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();

    if (isStatusChangeDialogVisible) return;

    const assignmentLink = getAssignmentLinkHref(content);

    onStreamLinkClick(assignmentLink, e)
      .then(() => {
        handleCardClickRedirect(e);
      })
      .catch(() => {
        handleCardClickRedirect(e);
      });
  };

  const handleStatusChangeCancel = (e: MouseEvent) => {
    eventService.send(
      isPastWorkSectionActive
        ? XApiEventKeys.assignmentStatusChangeCancelled
        : XApiEventKeys.assignmentStatusStillWorking,
      {
        result: {
          destinationUrl: getAssignmentLinkHref(content),
          assignmentStatus: isPastWorkSectionActive
            ? 'cancelled'
            : 'still-working',
        },
      },
      {
        result: {
          isRightClick: e.type === 'contextmenu',
          ...commonXapiContextPayload,
          ...assignmentDataForEventPayload,
        },
      },
    );

    setIsStatusChangeDialogVisible?.(false);
  };

  const handleUndoButtonClick = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setIsStatusChangeDialogVisible?.(true);
  };

  const handleToggleStatus = (e: MouseEvent) => {
    const assignmentLink = getAssignmentLinkHref(content);

    return onAssignmentComplete?.(assignmentLink, e);
  };

  if (isMiddleSchoolView) {
    return (
      <div
        className={clsx('stream-card', {
          'stream-card--middle-school': isMiddleSchoolView,
          'stream-card--disabled': isHandedInmCLASSAssessment,
        })}
        tabIndex={isHandedInmCLASSAssessment ? -1 : 0}
      >
        {/* Hidden element for screen readers */}
        <span
          className="u-hidden"
          aria-label={
            assignment.type === 'TaughtLessonAssignment'
              ? 'Taught Lesson'
              : 'Assignment'
          }
        />
        <div
          className="stream-card__details stream-card__details--middle-school"
          style={assignmentDetailsStyles}
        >
          <StreamCardContent
            assignment={assignment}
            grade={grade}
            onMsButtonClick={handleCardClick}
            msButtonHref={getAssignmentLinkHref(content)}
          />
        </div>
      </div>
    );
  }

  const renderWithWrapper = (children: JSX.Element) => {
    if (isHandedInmCLASSAssessment) {
      return children;
    }

    return (
      <Tooltip title={t('Open Lesson')} suppress={isStatusChangeDialogVisible}>
        {children}
      </Tooltip>
    );
  };

  return renderWithWrapper(
    <a
      className={clsx('stream-card', {
        'stream-card--middle-school': isMiddleSchoolView,
        'stream-card--removed-to-do': isCardRemovedFromTodo,
        'stream-card--dialog-visible': isStatusChangeDialogVisible,
        'stream-card--disabled': isHandedInmCLASSAssessment,
      })}
      onClick={handleCardClick}
      onContextMenu={handleCardClick}
      href={getAssignmentLinkHref(content)}
      rel="noreferrer"
      onKeyDown={handleSpaceClick}
      tabIndex={isHandedInmCLASSAssessment ? -1 : 0}
    >
      {/* Hidden element for screen readers */}
      <span
        className="u-hidden"
        aria-label={
          assignment.type === 'TaughtLessonAssignment'
            ? 'Taught Lesson'
            : 'Assignment'
        }
      />
      <StatusChangeDialog
        isVisible={isStatusChangeDialogVisible}
        cancelStatusChange={handleStatusChangeCancel}
        toggleStatus={handleToggleStatus}
        isCardRemovalAnimationInProgress={isCardRemovalAnimationInProgress}
        section={k5Section}
      />
      <div
        className={clsx('stream-card__details', {
          'stream-card__details--to-do': isToDoSectionActive,
          'stream-card__details--reduced-to-do':
            isStatusChangeDialogVisible && isToDoSectionActive,
          'stream-card__details--removed-to-do': isCardRemovedFromTodo,
          'stream-card__details--past-work': isPastWorkSectionActive,
          'stream-card__details--reduced-past-work': shouldReducePastWorkCard,
        })}
        style={assignmentDetailsStyles}
      >
        <StreamCardContent
          grade={grade}
          assignment={assignment}
          isStatusChangeDialogVisible={isStatusChangeDialogVisible}
          isCardRemovalAnimationInProgress={isCardRemovalAnimationInProgress}
          onUndoButtonClick={handleUndoButtonClick}
          k5Section={k5Section}
        />
      </div>
    </a>,
  );
};

export default StreamCard;
